import Geohash from 'latlon-geohash'
import { VENUE_HIGHLIGHT_SLUG_TO_LABEL, VENUE_HIGHLIGHT_LABEL_TO_SLUG } from '~/client/common/filters'
import { typesenseClient } from '~/client/common/typesense/client'
import { DEFAULT_SPACE_SORT_BY, type ListSpacesParams, type SpaceSortBy } from '~/shared/space/space-list.params'
import { toKV, PrivacyChoices, FileUploadCategoryChoices } from '~/shared/common/choices'
import { buildImageUrl } from '~/shared/common/image-url'
import type { RelaxedCapacityRange, RelaxableFilterKey } from '~/client/space/suggested-spaces-relaxation'
import { isFilterActive } from '~/client/space/suggested-spaces-relaxation'
import { VENUE_TRUST_SIGNAL_LABELS } from '~/shared/venue/trust-signals'

const DEFAULT_PAGE_SIZE = 20
export const SECONDARY_MAP_PAGE_SIZE = 250
const DEFAULT_SORT = 'priority_sort_score:desc,biblio_space_id:asc'
const SORT_BY_FIELD: Record<Exclude<SpaceSortBy, 'recommended' | 'distance'>, string> = {
  date_opened: 'venue.date_opened(missing_values:last):desc',
  capacity_low_to_high: 'capacity_average(missing_values:last):asc',
  capacity_high_to_low: 'capacity_average(missing_values:last):desc',
  google_rating: 'venue.google_rating_bayesian(missing_values:last):desc',
}
const SPACE_VENUE_STATUS_FIELDS = [
  'venue.is_closed',
  'venue.is_permanently_closed',
  'venue.is_temporarily_closed',
  'venue.permanently_closed',
  'venue.permanently_closed_aware_date',
  'venue.temporarily_closed_aware_date',
]

export interface TypesenseSpaceListOptions {
  signal?: AbortSignal
  countOnly?: boolean
}

export interface TypesenseSpaceDocument {
  id: number
  biblio_space_id?: number
  venue_id?: number
  venue_space_count?: number
  name: string
  slug: string
  standing_capacity: number
  seated_capacity: number
  assumed_capacity: number
  capacity_average?: number | null
  google_rating_bayesian?: number | null
  privacy_type: Record<string, string> | undefined
  priority_sort_score: number
  venue: {
    name: string
    slug: string
    cuisines: any[]
    neighborhood: any
    city: string
    display_address: string
    address_1: string
    google_place_id?: string
    price_point: 'high' | 'medium' | 'low' | 'budget' | undefined
    rating_google: number
    rating_google_count: number
    rating_data: any
    michelin_rating: any
    date_opened: string
    is_closed: boolean
    is_permanently_closed?: boolean
    is_temporarily_closed?: boolean
    permanently_closed?: boolean
    permanently_closed_aware_date?: string | null
    temporarily_closed_aware_date?: string | null
    is_new: boolean
    create_source: string
    has_event_menu: boolean
    has_event_kit: boolean
    has_floorplan: boolean
    latitude: number
    longitude: number
    blurb_bullets?: string[]
    blurb_bullets_sources?: { title: string; date: string; link: string; snippet: string }[]
    ai_filter_tags?: string[]
    trust_signals?: string[]
  }
  all_images: {
    id: number
    upload: string
    filename: string
    blurhash?: string
    category?: string
  }[]
  distance_from_point: number | null
}

export const getSpacesFromTypesense = async (params: ListSpacesParams, options?: TypesenseSpaceListOptions): Promise<{ data: TypesenseSpaceDocument[]; count: number }> => {
  const searchParams = buildSearchParams(params, options)

  const spacesResult = (await typesenseClient.collections('spaces').documents().search(searchParams, { abortSignal: options?.signal })) as any
  const spaces = processHits(spacesResult.hits)

  return { data: spaces, count: spacesResult.found }
}

export const countSpacesForVenues = async (venueIds: ReadonlyArray<number>, options?: { signal?: AbortSignal }): Promise<Record<number, number>> => {
  const ids = Array.from(new Set(venueIds.filter((id) => Number.isFinite(id) && id > 0)))
  const out: Record<number, number> = {}
  if (ids.length === 0) return out

  const searches = ids.map((venueId) => ({
    collection: 'spaces' as const,
    q: '*',
    page: 1,
    per_page: 0,
    filter_by: `venue_id:=${venueId}`,
  }))

  const response = (await typesenseClient.multiSearch.perform({ searches }, {}, { abortSignal: options?.signal })) as VenueCountMultiSearchResponse

  ids.forEach((venueId, index) => {
    const result = response.results[index]
    out[venueId] = typeof result?.found === 'number' ? result.found : 0
  })

  return out
}

// ── Secondary map spaces (lightweight, high-volume) ─────────────────────────

export interface SecondaryMapSpace {
  id: number
  venue_id: number
  venue: {
    latitude: number
    longitude: number
  }
}

/**
 * Fetch up to SECONDARY_MAP_PAGE_SIZE spaces with minimal fields for map dot rendering.
 * Only fetches coordinates and IDs, then dedupes by venue_id so each venue appears once.
 */
export const getSecondaryMapSpacesFromTypesense = async (params: ListSpacesParams, options?: { signal?: AbortSignal }): Promise<{ data: SecondaryMapSpace[]; count: number }> => {
  const filterClauses = buildFilterClauses(params)
  const filterBy = filterClauses.length ? filterClauses.join(' && ') : undefined

  const result = (await typesenseClient.collections('spaces').documents().search(
    {
      q: '*',
      page: 1,
      per_page: SECONDARY_MAP_PAGE_SIZE,
      filter_by: filterBy,
      sort_by: buildSortBy(params),
      include_fields: 'id,venue_id,$venues(latitude,longitude)',
    },
    { abortSignal: options?.signal }
  )) as any

  const hits: any[] = result.hits ?? []
  const seenVenueIds = new Set<number>()
  const data: SecondaryMapSpace[] = []

  for (const hit of hits) {
    const doc = hit.document
    const venueId = Number(doc.venue_id)
    if (seenVenueIds.has(venueId)) continue
    const venue = doc.venues ?? doc.venue ?? {}
    const lat = Number(venue.latitude)
    const lng = Number(venue.longitude)
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) continue
    seenVenueIds.add(venueId)
    data.push({
      id: Number(doc.id),
      venue_id: venueId,
      venue: { latitude: lat, longitude: lng },
    })
  }

  return { data, count: result.found ?? 0 }
}

/**
 * Fetch full space documents from Typesense by their IDs.
 * Used by shareable lists to enrich Supabase membership data with Typesense space data.
 */
export const getSpacesByIdsFromTypesense = async (spaceIds: number[]): Promise<TypesenseSpaceDocument[]> => {
  if (spaceIds.length === 0) return []

  const BATCH_SIZE = 250
  const batches = Math.ceil(spaceIds.length / BATCH_SIZE)

  const venueFields = [
    'name',
    'slug',
    'cuisines',
    'neighborhood',
    'city',
    'display_address',
    'address_1',
    'google_place_id',
    'has_event_kit',
    'has_event_menu',
    'has_floorplan',
    'price_point',
    'is_new',
    'rating_google',
    'rating_google_count',
    'michelin_rating',
    'date_opened',
    'create_source',
    'latitude',
    'longitude',
    'ai_filter_tags',
    'trust_signals',
    'blurb_bullets',
    'blurb_bullets_sources',
  ]

  const searches = Array.from({ length: batches }, (_, i) => {
    const batchIds = spaceIds.slice(i * BATCH_SIZE, (i + 1) * BATCH_SIZE)
    return {
      collection: 'spaces' as const,
      q: '*',
      filter_by: `id:=[${batchIds.join(',')}]`,
      per_page: BATCH_SIZE,
      include_fields: [
        'id',
        'venue_id',
        'name',
        'biblio_space_id',
        'standing_capacity',
        'seated_capacity',
        'assumed_capacity',
        'images',
        'venue_images',
        'privacy_type',
        'slug',
        'priority_sort_score',
        'distance_from_point',
        ...SPACE_VENUE_STATUS_FIELDS,
        `$venues(${venueFields.join(',')})`,
      ].join(','),
    }
  })

  const response = await typesenseClient.multiSearch.perform({ searches })

  const spaces: TypesenseSpaceDocument[] = []
  for (const result of response.results) {
    const hits = (result as any)?.hits ?? []
    spaces.push(...processHits(hits))
  }

  return spaces
}

export const getSpacesByVenueIdsFromTypesense = async (venueIds: number[]): Promise<TypesenseSpaceDocument[]> => {
  if (venueIds.length === 0) return []

  const BATCH_SIZE = 250
  const batches = Math.ceil(venueIds.length / BATCH_SIZE)

  const venueFields = [
    'name',
    'slug',
    'cuisines',
    'neighborhood',
    'city',
    'display_address',
    'address_1',
    'google_place_id',
    'has_event_kit',
    'has_event_menu',
    'has_floorplan',
    'price_point',
    'is_new',
    'rating_google',
    'rating_google_count',
    'michelin_rating',
    'date_opened',
    'create_source',
    'latitude',
    'longitude',
    'ai_filter_tags',
    'trust_signals',
    'blurb_bullets',
    'blurb_bullets_sources',
  ]

  const searches = Array.from({ length: batches }, (_, i) => {
    const batchIds = venueIds.slice(i * BATCH_SIZE, (i + 1) * BATCH_SIZE)
    return {
      collection: 'spaces' as const,
      q: '*',
      filter_by: `venue_id:=[${batchIds.join(',')}]`,
      per_page: BATCH_SIZE,
      sort_by: 'priority_sort_score:desc',
      include_fields: [
        'id',
        'name',
        'biblio_space_id',
        'standing_capacity',
        'seated_capacity',
        'images',
        'venue_images',
        'privacy_type',
        'slug',
        'priority_sort_score',
        'distance_from_point',
        ...SPACE_VENUE_STATUS_FIELDS,
        `$venues(${venueFields.join(',')})`,
      ].join(','),
    }
  })

  const response = await typesenseClient.multiSearch.perform({ searches })

  const spaces: TypesenseSpaceDocument[] = []
  for (const result of response.results) {
    const hits = (result as any)?.hits ?? []
    spaces.push(...processHits(hits))
  }

  return spaces
}

/**
 * Fetch all space IDs matching the given params.
 * Used to exclude original results from fuzzy/suggested spaces query.
 * Fetches up to 1000 IDs to avoid overly large filter clauses.
 */
export const getAllSpaceIdsFromTypesense = async (params: ListSpacesParams, options?: { signal?: AbortSignal }): Promise<number[]> => {
  const filterClauses = buildFilterClauses(params)
  const filterBy = filterClauses.length ? filterClauses.join(' && ') : undefined

  // First get the count
  const countResult = (await typesenseClient.collections('spaces').documents().search(
    {
      q: '*',
      page: 1,
      per_page: 0,
      filter_by: filterBy,
    },
    { abortSignal: options?.signal }
  )) as any

  const totalCount = countResult.found ?? 0

  if (totalCount === 0) {
    return []
  }

  // Fetch IDs in batches (Typesense max per_page is 250)
  const MAX_IDS = 1000 // Limit to avoid overly large filter clauses in fuzzy query
  const BATCH_SIZE = 250
  const pagesToFetch = Math.min(Math.ceil(Math.min(totalCount, MAX_IDS) / BATCH_SIZE), 4)

  const searches = Array.from({ length: pagesToFetch }, (_, i) => ({
    collection: 'spaces',
    q: '*',
    page: i + 1,
    per_page: BATCH_SIZE,
    filter_by: filterBy,
    include_fields: 'id',
    sort_by: buildSortBy(params),
  }))

  const response = await typesenseClient.multiSearch.perform({ searches }, {}, { abortSignal: options?.signal })

  const allIds: number[] = []
  for (const result of response.results) {
    const hits = (result as any)?.hits ?? []
    for (const hit of hits) {
      const id = Number(hit?.document?.id)
      if (Number.isFinite(id)) {
        allIds.push(id)
      }
    }
  }

  return allIds.slice(0, MAX_IDS)
}

export interface SuggestedSpacesOptions {
  signal?: AbortSignal
  excludeIds?: number[]
  limit?: number
  /** Target count for progressive filter relaxation (default: 100) */
  targetCount?: number
  activeVenueGroupId?: number
  /** Location latitude for geo filtering */
  locationLat?: number
  /** Location longitude for geo filtering */
  locationLng?: number
  /** Radius in miles for geo filtering (already expanded by caller) */
  radiusMiles?: number
  /** Pre-computed relaxed capacity range for standing (computed in useSuggestedSpaces hook) */
  standingCapacityRange?: RelaxedCapacityRange
  /** Pre-computed relaxed capacity range for seated (computed in useSuggestedSpaces hook) */
  seatedCapacityRange?: RelaxedCapacityRange
  /** Active relaxable filters from original params (computed in useSuggestedSpaces hook) */
  activeRelaxableFilters?: RelaxableFilterKey[]
  /** Original search params for applying relaxed "other filters" */
  originalParams?: ListSpacesParams
}

/**
 * Build filter clauses for suggested spaces, optionally excluding filters.
 * This is used to find which filter removal yields the most results.
 */
const buildSuggestedFilterClauses = (
  params: ListSpacesParams,
  options: {
    activeVenueGroupId: number
    excludeIds: number[]
    locationLat?: number
    locationLng?: number
    radiusMiles?: number
    /** Pre-computed relaxed capacity range for standing (only matches standing capacity) */
    standingCapacityRange?: RelaxedCapacityRange
    /** Pre-computed relaxed capacity range for seated (only matches seated capacity) */
    seatedCapacityRange?: RelaxedCapacityRange
    /** Array of filter keys to exclude (for progressive relaxation) */
    excludeFilterKeys?: RelaxableFilterKey[]
  }
): string[] => {
  const { activeVenueGroupId, excludeIds, locationLat, locationLng, radiusMiles, standingCapacityRange, seatedCapacityRange, excludeFilterKeys = [] } = options

  const allExcludedKeys = new Set(excludeFilterKeys)

  const filterClauses: string[] = []

  // Always apply venue group filter
  filterClauses.push(`venue.venue_group.id:=${activeVenueGroupId}`)

  // Exclude already-shown spaces
  if (excludeIds.length > 0) {
    filterClauses.push(`id:!=[${excludeIds.join(',')}]`)
  }

  // Add relaxed capacity filtering using pre-computed ranges with assumed_capacity fallback
  const bothFiltered = !!standingCapacityRange && !!seatedCapacityRange
  const capacityClauses: string[] = []

  if (standingCapacityRange) {
    const { min, max } = standingCapacityRange
    if (bothFiltered) {
      capacityClauses.push(`(standing_capacity:>=${min} && standing_capacity:<=${max})`)
    } else {
      capacityClauses.push(`((standing_capacity:>=${min} && standing_capacity:<=${max}) || (assumed_capacity:>=${min} && assumed_capacity:<=${max}))`)
    }
  }

  if (seatedCapacityRange) {
    const { min, max } = seatedCapacityRange
    if (bothFiltered) {
      capacityClauses.push(`(seated_capacity:>=${min} && seated_capacity:<=${max})`)
    } else {
      capacityClauses.push(`((seated_capacity:>=${min} && seated_capacity:<=${max}) || (assumed_capacity:>=${min} && assumed_capacity:<=${max}))`)
    }
  }

  if (capacityClauses.length > 0) {
    filterClauses.push(`(${capacityClauses.join(' || ')})`)
  }

  // Add geo filtering if location is provided (using expanded radius)
  if (typeof locationLat === 'number' && typeof locationLng === 'number' && typeof radiusMiles === 'number' && radiusMiles > 0) {
    const earthRadiusMiles = 3958.8
    const rad = radiusMiles / earthRadiusMiles
    const deg = (r: number) => (r * 180) / Math.PI
    const dLat = deg(rad)
    const dLng = deg(rad) / Math.max(Math.cos((locationLat * Math.PI) / 180), 0.00001)

    const clampLat = (v: number) => Math.max(-90, Math.min(90, v))
    const normLng = (v: number) => {
      let x = v
      while (x < -180) x += 360
      while (x > 180) x -= 360
      return x
    }

    const nwLat = clampLat(locationLat + dLat)
    const nwLng = normLng(locationLng - dLng)
    const seLat = clampLat(locationLat - dLat)
    const seLng = normLng(locationLng + dLng)

    try {
      const nw = Geohash.encode(nwLat, nwLng)
      const se = Geohash.encode(seLat, seLng)

      const northWest = Geohash.decode(nw)
      const southEast = Geohash.decode(se)

      const minLat = Math.min(northWest.lat, southEast.lat)
      const maxLat = Math.max(northWest.lat, southEast.lat)
      const minLng = Math.min(northWest.lon, southEast.lon)
      const maxLng = Math.max(northWest.lon, southEast.lon)

      filterClauses.push(`venue.location:(${maxLat}, ${minLng}, ${maxLat}, ${maxLng}, ${minLat}, ${maxLng}, ${minLat}, ${minLng})`)
    } catch {
      // If geohash encoding fails, proceed without geo filter
    }
  }

  // Apply "other filters" from original params (excluding specified filters)
  // Note: vibe_search is NOT included here - it's always removed for suggestions (Rule 3)

  const shouldApply = (key: RelaxableFilterKey) => !allExcludedKeys.has(key) && isFilterActive(params[key])

  // Neighborhoods
  if (shouldApply('neighborhoods')) {
    const neighborhoodIds = toStringArray(params.neighborhoods)
    const clause = buildMultiValueClause('venue.neighborhood.id', neighborhoodIds)
    if (clause) filterClauses.push(clause)
  }

  // Cuisines
  if (shouldApply('cuisines')) {
    const cuisineValues = toStringArray(params.cuisines)
    const clause = buildIdOrNameMultiValueClause('venue.cuisines.id', 'venue.cuisines.name', cuisineValues)
    if (clause) filterClauses.push(clause)
  }

  // Venue types
  if (shouldApply('venue_types')) {
    const venueTypeIds = toStringArray(params.venue_types)
    const clause = buildMultiValueClause('venue.venue_types.id', venueTypeIds)
    if (clause) filterClauses.push(clause)
  }

  // Price points
  if (shouldApply('prices')) {
    const pricePoints = toStringArray(params.prices)
    const clause = buildMultiValueClause('venue.price_point', pricePoints)
    if (clause) filterClauses.push(clause)
  }

  // Michelin ratings
  if (shouldApply('michelin_ratings')) {
    const michelinRatings = toStringArray(params.michelin_ratings)
    const clause = buildMultiValueClause('venue.michelin_rating', michelinRatings)
    if (clause) filterClauses.push(clause)
  }

  // Privacy
  if (shouldApply('privacy')) {
    const privacyFilters = toStringArray(params.privacy)
    const clause = buildMultiValueClause('privacy_type', privacyFilters)
    if (clause) filterClauses.push(clause)
  }

  // Noise
  if (shouldApply('noise')) {
    const noiseFilters = toStringArray(params.noise)
    const clause = buildMultiValueClause('noise_privacy', noiseFilters)
    if (clause) filterClauses.push(clause)
  }

  // Space types
  if (shouldApply('space_types')) {
    const spaceTypes = toStringArray(params.space_types)
    const clause = buildMultiValueClause('space_types', spaceTypes)
    if (clause) filterClauses.push(clause)
  }

  // Outdoor space types
  if (shouldApply('outdoor_space_types')) {
    const outdoorSpaceTypes = toStringArray(params.outdoor_space_types)
    const clause = buildMultiValueClause('outdoor_space_type', outdoorSpaceTypes)
    if (clause) filterClauses.push(clause)
  }

  // Windows
  if (shouldApply('windows')) {
    const windowOptions = toStringArray(params.windows)
    const clause = buildMultiValueClause('windows', windowOptions)
    if (clause) filterClauses.push(clause)
  }

  // Amenities
  if (shouldApply('amenities')) {
    const amenities = toStringArray(params.amenities)
    const clause = buildMultiValueClause('amenities', amenities)
    if (clause) filterClauses.push(clause)
  }

  // AV
  if (shouldApply('av')) {
    const avOptions = toStringArray(params.av)
    const clause = buildMultiValueClause('space_media', avOptions)
    if (clause) filterClauses.push(clause)
  }

  // Min Google rating
  if (shouldApply('min_google_rating')) {
    const minGoogleRating = toNumber(params.min_google_rating)
    if (typeof minGoogleRating === 'number') {
      filterClauses.push(`venue.rating_google:>=${minGoogleRating}`)
    }
  }

  // F&B minimum (from)
  if (shouldApply('f_and_b_minimum_from')) {
    const fnbFrom = toNumber(params.f_and_b_minimum_from)
    if (typeof fnbFrom === 'number') {
      filterClauses.push(`f_and_b_minimum:>=${fnbFrom}`)
    }
  }

  // F&B minimum (to)
  if (shouldApply('f_and_b_minimum_to')) {
    const fnbTo = toNumber(params.f_and_b_minimum_to)
    if (typeof fnbTo === 'number') {
      filterClauses.push(`f_and_b_minimum:<=${fnbTo}`)
    }
  }

  return filterClauses
}

/**
 * Fetch suggested spaces with relaxed filters.
 * Used for "You may also be interested in" section to fill pagination with fuzzy results.
 *
 * Relaxation rules applied:
 * - Location: 2x radius (expanded by caller)
 * - Capacity: X ± 100% for both standing AND seated
 * - Vibe: Always removed (Rule 3)
 * - Other filters: Progressively removes filters until targetCount is reached (Rule 4)
 *   - Removes the filter that restricts the most results first
 */
export interface SuggestedSpacesResult {
  data: TypesenseSpaceDocument[]
  count: number
  /** Debug info about relaxed filters */
  relaxationInfo: {
    /** Active "other filters" from original params */
    activeOtherFilters: string[]
    /** Filters that were progressively removed to reach target count */
    removedFilters: string[]
    /** Expanded radius used (2x original) */
    expandedRadiusMiles?: number
    /** Capacity relaxation (X ± 100%) */
    capacityRelaxation?: {
      standingRange?: { min: number; max: number }
      seatedRange?: { min: number; max: number }
    }
  }
}

export const getSuggestedSpacesFromTypesense = async (options: SuggestedSpacesOptions): Promise<SuggestedSpacesResult> => {
  const {
    signal,
    excludeIds = [],
    limit = 100,
    targetCount = 100,
    activeVenueGroupId = 1,
    locationLat,
    locationLng,
    radiusMiles,
    standingCapacityRange,
    seatedCapacityRange,
    activeRelaxableFilters = [],
    originalParams,
  } = options

  const baseFilterOptions = {
    activeVenueGroupId,
    excludeIds,
    locationLat,
    locationLng,
    radiusMiles,
    standingCapacityRange,
    seatedCapacityRange,
  }

  // Build capacity relaxation info for debugging (using pre-computed ranges from hook)
  const capacityRelaxation: SuggestedSpacesResult['relaxationInfo']['capacityRelaxation'] = {}
  if (standingCapacityRange) {
    capacityRelaxation.standingRange = standingCapacityRange
  }
  if (seatedCapacityRange) {
    capacityRelaxation.seatedRange = seatedCapacityRange
  }

  // If no original params, use simple filters (backward compatibility)
  if (!originalParams) {
    const filterClauses = buildSuggestedFilterClauses({} as ListSpacesParams, baseFilterOptions)
    const result = await executeSuggestedSpacesQuery(filterClauses, limit)
    return {
      ...result,
      relaxationInfo: {
        activeOtherFilters: [],
        removedFilters: [],
        expandedRadiusMiles: radiusMiles,
        capacityRelaxation: Object.keys(capacityRelaxation).length > 0 ? capacityRelaxation : undefined,
      },
    }
  }

  // Use active filters passed from hook (computed in useSuggestedSpaces)
  const activeOtherFilters = activeRelaxableFilters

  // Track which filters have been removed during progressive relaxation
  const removedFilters: RelaxableFilterKey[] = []

  // Progressive filter relaxation loop
  while (true) {
    // Build filter clauses with currently removed filters
    const filterClauses = buildSuggestedFilterClauses(originalParams, {
      ...baseFilterOptions,
      excludeFilterKeys: removedFilters,
    })

    // Get count with current filters
    // Uses multiSearch (POST body) because excludeIds can produce very long filter_by strings
    // that exceed URL length limits for single .search() GET requests
    const countResponse = await typesenseClient.multiSearch.perform(
      {
        searches: [
          {
            collection: 'spaces',
            q: '*',
            page: 1,
            per_page: 0, // Count only
            filter_by: filterClauses.join(' && '),
          },
        ],
      },
      {},
      { abortSignal: signal }
    )

    const currentCount = (countResponse.results[0] as any)?.found ?? 0

    // If we have enough results, execute final query and return
    if (currentCount >= targetCount) {
      const result = await executeSuggestedSpacesQuery(filterClauses, limit)
      return {
        ...result,
        relaxationInfo: {
          activeOtherFilters: [...activeOtherFilters],
          removedFilters: [...removedFilters],
          expandedRadiusMiles: radiusMiles,
          capacityRelaxation: Object.keys(capacityRelaxation).length > 0 ? capacityRelaxation : undefined,
        },
      }
    }

    // Get remaining active filters that haven't been removed yet
    const remainingFilters = activeOtherFilters.filter((f) => !removedFilters.includes(f))

    // If no more filters to remove, return what we have
    if (remainingFilters.length === 0) {
      const result = await executeSuggestedSpacesQuery(filterClauses, limit)
      return {
        ...result,
        relaxationInfo: {
          activeOtherFilters: [...activeOtherFilters],
          removedFilters: [...removedFilters],
          expandedRadiusMiles: radiusMiles,
          capacityRelaxation: Object.keys(capacityRelaxation).length > 0 ? capacityRelaxation : undefined,
        },
      }
    }

    // Find which filter removal yields the most results
    const countQueries = remainingFilters.map((filterKey) => {
      const testFilterClauses = buildSuggestedFilterClauses(originalParams, {
        ...baseFilterOptions,
        excludeFilterKeys: [...removedFilters, filterKey],
      })
      return {
        filterKey,
        filterClauses: testFilterClauses,
      }
    })

    // Execute count queries in parallel
    const searches = countQueries.map(({ filterClauses: fc }) => ({
      collection: 'spaces',
      q: '*',
      page: 1,
      per_page: 0, // Count only
      filter_by: fc.join(' && '),
    }))

    const filterCountResponse = await typesenseClient.multiSearch.perform({ searches }, {}, { abortSignal: signal })

    // Find the filter whose removal gives the most results
    let bestFilterToRemove: RelaxableFilterKey | undefined
    let bestCount = -1

    filterCountResponse.results.forEach((result: any, index: number) => {
      const count = result.found ?? 0
      if (count > bestCount) {
        bestCount = count
        bestFilterToRemove = countQueries[index].filterKey
      }
    })

    // If we found a filter to remove, add it to the removed list and continue
    if (bestFilterToRemove) {
      removedFilters.push(bestFilterToRemove)
    } else {
      // No filter found (shouldn't happen, but handle gracefully)
      const filterClauses = buildSuggestedFilterClauses(originalParams, {
        ...baseFilterOptions,
        excludeFilterKeys: removedFilters,
      })
      const result = await executeSuggestedSpacesQuery(filterClauses, limit)
      return {
        ...result,
        relaxationInfo: {
          activeOtherFilters: [...activeOtherFilters],
          removedFilters: [...removedFilters],
          expandedRadiusMiles: radiusMiles,
          capacityRelaxation: Object.keys(capacityRelaxation).length > 0 ? capacityRelaxation : undefined,
        },
      }
    }
  }
}

/**
 * Execute the final suggested spaces query with the given filter clauses.
 */
const executeSuggestedSpacesQuery = async (filterClauses: string[], limit: number): Promise<{ data: TypesenseSpaceDocument[]; count: number }> => {
  const filterBy = filterClauses.join(' && ')

  const searchParams: any = {
    q: '*',
    page: 1,
    per_page: limit,
    filter_by: filterBy,
    sort_by: DEFAULT_SORT,
    include_fields: [
      'id',
      'venue_id',
      'name',
      'biblio_space_id',
      'standing_capacity',
      'seated_capacity',
      'assumed_capacity',
      'images',
      'venue_images',
      'privacy_type',
      'slug',
      'priority_sort_score',
      'distance_from_point',
      'venue.venue_group.id',
      ...SPACE_VENUE_STATUS_FIELDS,
      '$venues(name, slug, cuisines, neighborhood, city, display_address, address_1, has_event_kit, has_event_menu, has_floorplan, price_point, is_new, rating_google, rating_google_count, michelin_rating, date_opened, create_source, latitude, longitude, trust_signals)',
    ].join(','),
  }

  // Uses multiSearch (POST body) because excludeIds can produce very long filter_by strings
  // that exceed URL length limits for single .search() GET requests
  const response = await typesenseClient.multiSearch.perform({
    searches: [
      {
        collection: 'spaces',
        ...searchParams,
      },
    ],
  })

  const spacesResult = response.results[0] as any
  const spaces = processHits(spacesResult.hits)

  return { data: spaces, count: spacesResult.found }
}

const processHits = (hits: any[]): TypesenseSpaceDocument[] => {
  if (!hits) return []

  return hits
    .map((hit: any, index: number): TypesenseSpaceDocument | null => {
      try {
        const doc = hit.document

        const spaceImagesRaw = Array.isArray(doc?.images) ? doc?.images : []
        const venueImagesRaw = Array.isArray(doc?.venue_images) ? doc?.venue_images : []
        const allImagesRaw = [...spaceImagesRaw, ...venueImagesRaw]

        // Filter space images (category = space_image) and exclude floorplans
        // Note: Fields in images collection are nested under 'image.' prefix (e.g., image.category, image.upload)
        const images = allImagesRaw
          .filter((img: any) => !img?.['sub_category']?.includes('floorplan'))
          .map((img: any) => ({
            id: img?.['id'],
            upload: img?.['upload'],
            filename: img?.['filename'],
            blurhash: img?.['blurhash'],
            url: img?.['upload'] ? buildImageUrl(img['upload'], { w: 283, h: 283 }) : '',
            category: FileUploadCategoryChoices[img?.['category']],
          }))

        // Use joined venues for display fields, but read closure status from the spaces document.
        const joinedVenue = doc.venues || {}

        const result: TypesenseSpaceDocument = {
          id: Number(doc.id),
          biblio_space_id: doc.biblio_space_id != null ? Number(doc.biblio_space_id) : undefined,
          venue_id: doc.venue_id != null ? Number(doc.venue_id) : undefined,
          name: doc.name,
          standing_capacity: doc.standing_capacity,
          seated_capacity: doc.seated_capacity,
          assumed_capacity: doc.assumed_capacity ?? 0,
          capacity_average: typeof doc.capacity_average === 'number' ? doc.capacity_average : null,
          google_rating_bayesian:
            typeof joinedVenue.google_rating_bayesian === 'number' ? joinedVenue.google_rating_bayesian : null,
          privacy_type: toKV(doc.privacy_type, PrivacyChoices),
          priority_sort_score: doc.priority_sort_score,
          slug: doc.slug,
          venue: {
            name: joinedVenue.name,
            slug: joinedVenue.slug,
            cuisines: joinedVenue.cuisines,
            neighborhood: joinedVenue.neighborhood,
            city: joinedVenue.city,
            display_address: joinedVenue.display_address,
            address_1: joinedVenue.address_1,
            google_place_id: joinedVenue.google_place_id,
            price_point: joinedVenue.price_point,
            rating_google: joinedVenue.rating_google,
            rating_google_count: joinedVenue.rating_google_count,
            rating_data: joinedVenue.rating_data,
            michelin_rating: joinedVenue.michelin_rating,
            date_opened: joinedVenue.date_opened,
            is_closed: doc['venue.is_closed'] ?? false,
            is_permanently_closed: doc['venue.is_permanently_closed'] ?? false,
            is_temporarily_closed: doc['venue.is_temporarily_closed'] ?? false,
            permanently_closed: doc['venue.permanently_closed'] ?? false,
            permanently_closed_aware_date: doc['venue.permanently_closed_aware_date'] ?? null,
            temporarily_closed_aware_date: doc['venue.temporarily_closed_aware_date'] ?? null,
            is_new: joinedVenue.is_new ?? false,
            create_source: joinedVenue.create_source,
            latitude: joinedVenue.latitude,
            longitude: joinedVenue.longitude,
            has_event_menu: joinedVenue.has_event_menu,
            has_event_kit: joinedVenue.has_event_kit,
            has_floorplan: joinedVenue.has_floorplan,
            ai_filter_tags: joinedVenue.ai_filter_tags,
            trust_signals: joinedVenue.trust_signals,
            blurb_bullets: joinedVenue.blurb_bullets,
            blurb_bullets_sources: joinedVenue.blurb_bullets_sources,
          },
          all_images: images,
          distance_from_point: doc.distance_from_point ?? null,
        }

        return result
      } catch (error) {
        console.error(`Error processing hit at index ${index}:`, error, hit)
        return null
      }
    })
    .filter(Boolean) as TypesenseSpaceDocument[]
}

export const buildSortBy = (params: ListSpacesParams): string => {
  const sortBy = params.sort_by ?? DEFAULT_SPACE_SORT_BY

  if (sortBy === 'distance') {
    const lat = typeof params.sort_origin_lat === 'number' ? params.sort_origin_lat : NaN
    const lng = typeof params.sort_origin_lng === 'number' ? params.sort_origin_lng : NaN

    if (Number.isFinite(lat) && Number.isFinite(lng)) {
      return `venue.location(${lat},${lng}):asc,${DEFAULT_SORT}`
    }

    return DEFAULT_SORT
  }

  if (sortBy === DEFAULT_SPACE_SORT_BY) return DEFAULT_SORT

  return `${SORT_BY_FIELD[sortBy]},${DEFAULT_SORT}`
}

const buildSearchParams = (params: ListSpacesParams, options?: TypesenseSpaceListOptions) => {
  const page = toPositiveNumber(params.page) ?? 1
  const perPage = options?.countOnly ? 0 : (params.perPage ?? DEFAULT_PAGE_SIZE)
  const filterClauses = buildFilterClauses(params)
  const filterBy = filterClauses.length ? filterClauses.join(' && ') : undefined
  const venueFields = [
    'name',
    'slug',
    'cuisines',
    'neighborhood',
    'city',
    'display_address',
    'address_1',
    'google_place_id',
    'has_event_kit',
    'has_event_menu',
    'has_floorplan',
    'price_point',
    'is_new',
    'rating_google',
    'rating_google_count',
    'google_rating_bayesian',
    'michelin_rating',
    'date_opened',
    'create_source',
    'latitude',
    'longitude',
    'ai_filter_tags',
    'trust_signals',
    'blurb_bullets',
    'blurb_bullets_sources',
  ]
  const searchParams: any = {
    q: '*',
    page,
    per_page: perPage,
    filter_by: filterBy,
    sort_by: buildSortBy(params),
    include_fields: [
      'id',
      'venue_id',
      'name',
      'biblio_space_id',
      'standing_capacity',
      'seated_capacity',
      'assumed_capacity',
      'capacity_average',
      'images',
      'venue_images',
      'privacy_type',
      'slug',
      'priority_sort_score',
      'distance_from_point',
      'venue.venue_group.id',
      ...SPACE_VENUE_STATUS_FIELDS,
      // JOIN venues - only fetch fields used in TypesenseSpaceDocument
      `$venues(${venueFields.join(',')})`,
    ].join(','),
  }

  return searchParams
}

export const buildFilterClauses = (params: ListSpacesParams): string[] => {
  const clauses: string[] = []
  const addClause = (clause?: string) => {
    if (clause) clauses.push(clause)
  }

  const googlePlaceIdClause = buildEqualityClause('venue.google_place_id', params.google_place_id)
  // A selected Google place is an exact venue lookup. Do not add venue-group
  // scoping or any user filters, since those can hide the intended venue.
  if (googlePlaceIdClause) return [googlePlaceIdClause]

  const activeVenueGroupId = params.active_venue_group_id ?? 1
  addClause(`venue.venue_group.id:=${activeVenueGroupId}`)

  // Venue tag filter — restrict to the union of venues that carry the selected
  // tags. Empty array means "user picked a tag but nothing matches" → force
  // zero results so we don't silently fall through to the unfiltered set.
  if (Array.isArray(params.venue_ids)) {
    if (params.venue_ids.length === 0) {
      addClause('venue_id:=-1')
    } else {
      addClause(`venue_id:=[${params.venue_ids.join(',')}]`)
    }
  }

  const neighborhoodIds = toStringArray(params.neighborhoods)
  addClause(buildMultiValueClause('venue.neighborhood.id', neighborhoodIds))

  const cuisineValues = toStringArray(params.cuisines)
  addClause(buildIdOrNameMultiValueClause('venue.cuisines.id', 'venue.cuisines.name', cuisineValues))

  const venueTypeIds = toStringArray(params.venue_types)
  addClause(buildMultiValueClause('venue.venue_types.id', venueTypeIds))

  const pricePoints = toStringArray(params.prices)
  addClause(buildMultiValueClause('venue.price_point', pricePoints))

  const michelinRatings = toStringArray(params.michelin_ratings)
  addClause(buildMultiValueClause('venue.michelin_rating', michelinRatings))

  const privacyFilters = toStringArray(params.privacy)
  addClause(buildMultiValueClause('privacy_type', privacyFilters))

  const noiseFilters = toStringArray(params.noise)
  addClause(buildMultiValueClause('noise_privacy', noiseFilters))

  const spaceTypes = toStringArray(params.space_types)
  addClause(buildMultiValueClause('space_types', spaceTypes))

  const outdoorSpaceTypes = toStringArray(params.outdoor_space_types)
  addClause(buildMultiValueClause('outdoor_space_type', outdoorSpaceTypes))

  const windowOptions = toStringArray(params.windows)
  addClause(buildMultiValueClause('windows', windowOptions))

  const amenities = toStringArray(params.amenities)
  addClause(buildMultiValueClause('amenities', amenities))

  const avOptions = toStringArray(params.av)
  addClause(buildMultiValueClause('space_media', avOptions))

  const aiFilterTags = toStringArray(params.ai_filter_tags)
  // AND logic: venue must have ALL selected tags (one clause per tag)
  for (const tag of aiFilterTags) {
    addClause(`venue.ai_filter_tags:=${formatFilterValue(tag)}`)
  }

  const trustSignals = toStringArray(params.trust_signals)
  addClause(buildMultiValueClause('venue.trust_signals', trustSignals))

  if (params.venue_management_group_caliber === 'high') {
    addClause('$venues(venue_management_group_caliber:=high)')
  }

  if (typeof params.wheelchair === 'boolean') {
    addClause(`wheelchair_accesible:=${params.wheelchair}`)
  }

  const fnbFrom = toNumber(params.f_and_b_minimum_from)
  if (typeof fnbFrom === 'number') {
    addClause(`f_and_b_minimum:>=${fnbFrom}`)
  }

  const fnbTo = toNumber(params.f_and_b_minimum_to)
  if (typeof fnbTo === 'number') {
    addClause(`f_and_b_minimum:<=${fnbTo}`)
  }

  const minGoogleRating = toNumber(params.min_google_rating)
  if (typeof minGoogleRating === 'number') {
    addClause(`venue.rating_google:>=${minGoogleRating}`)
  }

  const standingGuestCount = toNumber(params.standing_guest_count)
  const seatedGuestCount = toNumber(params.seated_guest_count)
  const bothFiltered = typeof standingGuestCount === 'number' && typeof seatedGuestCount === 'number'
  const capacityClauses: string[] = []

  if (typeof standingGuestCount === 'number') {
    const { min, max } = capacityRange(standingGuestCount)
    if (bothFiltered) {
      capacityClauses.push(`(standing_capacity:>=${min} && standing_capacity:<=${max})`)
    } else {
      capacityClauses.push(`((standing_capacity:>=${min} && standing_capacity:<=${max}) || (assumed_capacity:>=${min} && assumed_capacity:<=${max}))`)
    }
  }

  if (typeof seatedGuestCount === 'number') {
    const { min, max } = capacityRange(seatedGuestCount)
    if (bothFiltered) {
      capacityClauses.push(`(seated_capacity:>=${min} && seated_capacity:<=${max})`)
    } else {
      capacityClauses.push(`((seated_capacity:>=${min} && seated_capacity:<=${max}) || (assumed_capacity:>=${min} && assumed_capacity:<=${max}))`)
    }
  }

  if (capacityClauses.length) {
    const capacityFilter = `(${capacityClauses.join(' || ')})`
    addClause(capacityFilter)
  }

  if (params.nw && params.se) {
    const boundingBoxClauses = buildBoundingBoxClauses(params.nw, params.se)
    boundingBoxClauses.forEach(addClause)
  }

  return clauses
}

const buildEqualityClause = (field: string, rawValue: string | number | null | undefined) => {
  if (rawValue === null || rawValue === undefined || rawValue === '') return undefined
  return `${field}:=${formatFilterValue(rawValue)}`
}

const buildMultiValueClause = (field: string, values: string[]) => {
  if (!values.length) return undefined
  const formatted = values.map(formatFilterValue).join(',')
  return `${field}:=[${formatted}]`
}

const isIntegerString = (value: string): boolean => /^\d+$/.test(value)

const buildIdOrNameMultiValueClause = (idField: string, nameField: string, values: string[]) => {
  if (!values.length) return undefined

  const idValues: string[] = []
  const nameValues: string[] = []
  for (const value of values) {
    if (isIntegerString(value)) idValues.push(value)
    else nameValues.push(value)
  }

  const clauses = [
    buildMultiValueClause(idField, idValues),
    buildMultiValueClause(nameField, nameValues),
  ].filter((clause): clause is string => typeof clause === 'string')

  if (clauses.length === 0) return undefined
  if (clauses.length === 1) return clauses[0]
  return `(${clauses.join(' || ')})`
}

const buildGreaterThanOrEqualToClause = (field: string, rawValue: string | number | null | undefined) => {
  if (rawValue === null || rawValue === undefined || rawValue === '') return undefined
  return `${field}:>=${formatFilterValue(rawValue)}`
}

const buildLessThanOrEqualToClause = (field: string, rawValue: string | number | null | undefined) => {
  if (rawValue === null || rawValue === undefined || rawValue === '') return undefined
  return `${field}:<=${formatFilterValue(rawValue)}`
}

const buildBoundingBoxClauses = (nw: string, se: string) => {
  try {
    const northWest = Geohash.decode(nw)
    const southEast = Geohash.decode(se)

    const minLat = Math.min(northWest.lat, southEast.lat)
    const maxLat = Math.max(northWest.lat, southEast.lat)
    const minLng = Math.min(northWest.lon, southEast.lon)
    const maxLng = Math.max(northWest.lon, southEast.lon)

    return [`venue.location:(${maxLat}, ${minLng}, ${maxLat}, ${maxLng}, ${minLat}, ${maxLng}, ${minLat}, ${minLng})`]
  } catch {
    return []
  }
}

const capacityRange = (value: number) => {
  const min = Math.max(0, Math.round(value * 0.85))
  const max = Math.max(min, Math.round(value * 1.8))
  return { min, max }
}

const toPositiveNumber = (value: unknown): number | undefined => {
  const num = toNumber(value)
  if (typeof num === 'number' && num > 0) {
    return Math.floor(num)
  }
  return undefined
}

const toNumber = (value: unknown): number | undefined => {
  if (typeof value === 'number' && Number.isFinite(value)) return value
  if (typeof value === 'string' && value.trim().length > 0) {
    const parsed = Number(value)
    if (Number.isFinite(parsed)) {
      return parsed
    }
  }
  return undefined
}

const toStringArray = (value: unknown): string[] => {
  if (!Array.isArray(value)) return []
  return value.map((entry) => toTrimmedString(entry)).filter((entry): entry is string => typeof entry === 'string' && entry.length > 0)
}

const toTrimmedString = (value: unknown): string | undefined => {
  if (typeof value === 'string') {
    const trimmed = value.trim()
    return trimmed.length ? trimmed : undefined
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(value)
  }
  return undefined
}
const formatFilterValue = (value: string | number) => {
  if (typeof value === 'number') return value
  const sanitized = value.replace(/"/g, '\\"')
  return `"${sanitized}"`
}

// Facet field mapping - maps filter categories to Typesense field names
export const FACET_FIELDS = {
  cuisines: 'venue.cuisines.name',
  venueTypes: 'venue.venue_types.name',
  neighborhoods: 'venue.neighborhood.name',
  pricePoints: 'venue.price_point',
  michelinRatings: 'venue.michelin_rating',
  googleRating: 'venue.rating_google',
  spaceTypes: 'space_types',
  privacyTypes: 'privacy_type',
  noisePrivacy: 'noise_privacy',
  amenities: 'amenities',
  av: 'space_media',
  outdoor: 'outdoor_space_type',
  windows: 'windows',
  venueHighlights: 'venue.ai_filter_tags',
  editorialRecognition: 'venue.trust_signals',
  topHospitalityGroups: '$venues(venue_management_group_caliber)',
} as const

export type FacetField = keyof typeof FACET_FIELDS

export interface FacetCount {
  value: string
  count: number
}

export interface FacetCounts {
  cuisines: Record<string, number>
  venueTypes: Record<string, number>
  neighborhoods: Record<string, number>
  pricePoints: Record<string, number>
  michelinRatings: Record<string, number>
  googleRating: Record<string, number>
  spaceTypes: Record<string, number>
  privacyTypes: Record<string, number>
  noisePrivacy: Record<string, number>
  amenities: Record<string, number>
  av: Record<string, number>
  outdoor: Record<string, number>
  windows: Record<string, number>
  venueHighlights: Record<string, number>
  editorialRecognition: Record<string, number>
  topHospitalityGroups: Record<string, number>
}

// Reverse mappings: slug -> label for space-related fields
// These map from Typesense values back to UI display labels
const SLUG_TO_SPACE_TYPE: Record<string, string> = {
  private_dining_room: 'Private Dining Room',
  venue_buyout: 'Venue Buyout',
  main_dining_room: 'Main Dining Room',
  bar_lounge: 'Bar/Lounge',
  rooftop_terrace: 'Rooftop/Terrace',
  ballroom: 'Ballroom',
  boat_yacht: 'Boat/Yacht',
  prefunction_space: 'Prefunction Space',
  loft: 'Loft',
  night_club: 'Night Club',
  raw_space: 'Raw Space',
  balcony: 'Balcony',
  nook: 'Nook',
  mezzanine: 'Mezzanine',
  section: 'Section',
  atrium: 'Atrium',
  garden: 'Garden',
  restaurant: 'Restaurant',
  auditorium: 'Auditorium',
  boardroom: 'Boardroom',
}

const SLUG_TO_PRIVACY: Record<string, string> = {
  buyout: 'Buyout',
  private: 'Private',
  practically_private: 'Practically Private',
  semi_private: 'Semi-Private',
  main_dining_area: 'Main Dining Area',
}

const SLUG_TO_NOISE: Record<string, string> = {
  pin_drop: 'Pin Drop',
  quiet: 'Quiet',
  moderate: 'Moderate',
  boisterous: 'Boisterous',
  na: 'N/A (Buyout)',
}

const SLUG_TO_AMENITY: Record<string, string> = {
  private_entrance: 'Private entrance',
  outdoor_space_rooftop: 'Outdoor space / Rooftop',
  private_bar: 'Private bar',
  ada_compliant: 'ADA Compliant',
  street_level_access: 'Street Level Access',
  stage: 'Stage',
  dance_floor: 'Dance Floor',
  private_restroom: 'Private restroom',
}

const SLUG_TO_AV: Record<string, string> = {
  projector_tv: 'Projector / TV',
  wifi: 'Wi-Fi',
  inhouse_sound_system: 'In-house sound system',
  audio_line_in: 'Audio Line-In',
  microphone: 'Microphone',
}

const SLUG_TO_OUTDOOR: Record<string, string> = {
  full_outdoor_no_cover: 'Full Outdoor, No Cover',
  full_outdoor_cover: 'Full Outdoor, Cover',
  indoor_outdoor: 'Indoor / Outdoor',
  no_outdoor: 'No Outdoor',
}

const SLUG_TO_WINDOWS: Record<string, string> = {
  outdoor: 'Outdoor-facing',
  indoor: 'Indoor-facing',
  skylight: 'Skylight',
  na: 'N/A',
  none: 'None',
}

const SLUG_TO_PRICE_POINT: Record<string, string> = {
  budget: '1',
  low: '2',
  medium: '3',
  high: '4',
}

const SLUG_TO_MICHELIN: Record<string, string> = {
  '3': '3 Stars',
  '2': '2 Stars',
  '1': '1 Star',
  bib: 'Bib Gourmand',
}

// Map a facet category to its value-to-label mapping
const getCategoryMapping = (category: keyof FacetCounts): Record<string, string> | null => {
  switch (category) {
    case 'spaceTypes':
      return SLUG_TO_SPACE_TYPE
    case 'privacyTypes':
      return SLUG_TO_PRIVACY
    case 'noisePrivacy':
      return SLUG_TO_NOISE
    case 'amenities':
      return SLUG_TO_AMENITY
    case 'av':
      return SLUG_TO_AV
    case 'outdoor':
      return SLUG_TO_OUTDOOR
    case 'windows':
      return SLUG_TO_WINDOWS
    case 'pricePoints':
      return SLUG_TO_PRICE_POINT
    case 'michelinRatings':
      return SLUG_TO_MICHELIN
    case 'venueHighlights':
      return VENUE_HIGHLIGHT_LABEL_TO_SLUG
    case 'editorialRecognition':
      return VENUE_TRUST_SIGNAL_LABELS
    // These fields use names directly, no mapping needed
    case 'topHospitalityGroups':
    case 'cuisines':
    case 'venueTypes':
    case 'neighborhoods':
      return null
    default:
      return null
  }
}

// Map facet categories to their corresponding ListSpacesParams keys
// Used to exclude a category's filters when computing its own facet counts (disjunctive faceting)
const FACET_CATEGORY_TO_PARAM_KEY: Record<keyof FacetCounts, keyof ListSpacesParams | null> = {
  cuisines: 'cuisines',
  venueTypes: 'venue_types',
  neighborhoods: 'neighborhoods',
  pricePoints: 'prices',
  michelinRatings: 'michelin_ratings',
  googleRating: 'min_google_rating',
  spaceTypes: 'space_types',
  privacyTypes: 'privacy',
  noisePrivacy: 'noise',
  amenities: 'amenities',
  av: 'av',
  outdoor: 'outdoor_space_types',
  windows: 'windows',
  venueHighlights: 'ai_filter_tags',
  editorialRecognition: 'trust_signals',
  topHospitalityGroups: 'venue_management_group_caliber',
}

/**
 * Create a copy of params with a specific category's filters removed.
 * Used for disjunctive faceting - counts within a category should reflect
 * what would happen if you selected that option, not the intersection with existing selections.
 */
const createParamsExcludingCategory = (params: ListSpacesParams, category: keyof FacetCounts): ListSpacesParams => {
  const paramKey = FACET_CATEGORY_TO_PARAM_KEY[category]
  if (!paramKey) return params

  const newParams = { ...params }
  delete newParams[paramKey]
  return newParams
}

/**
 * Fetch facet counts for all filter fields given current filter params.
 * Uses disjunctive faceting: counts for options within a category are computed
 * WITHOUT that category's current filters applied. This means selecting "Venue Buyout"
 * won't change the count shown for "Private Dining Room" in the same category.
 *
 * Values are mapped back to UI display labels.
 */
export const getFacetCountsFromTypesense = async (params: ListSpacesParams, options?: { signal?: AbortSignal }): Promise<FacetCounts> => {
  const categories = Object.entries(FACET_FIELDS) as Array<[keyof FacetCounts, string]>
  const searches = categories.map(([categoryKey, facetField]) => {
    const paramsForCategory = createParamsExcludingCategory(params, categoryKey)
    const filterClauses = buildFilterClauses(paramsForCategory)
    const filterBy = filterClauses.length ? filterClauses.join(' && ') : undefined

    return {
      collection: 'spaces',
      q: '*',
      page: 1,
      per_page: 0,
      filter_by: filterBy,
      facet_by: facetField,
      max_facet_values: 100,
    }
  })

  const response = await typesenseClient.multiSearch.perform({ searches }, {}, { abortSignal: options?.signal })

  const facetCounts: FacetCounts = {
    cuisines: {},
    venueTypes: {},
    neighborhoods: {},
    pricePoints: {},
    michelinRatings: {},
    googleRating: {},
    spaceTypes: {},
    privacyTypes: {},
    noisePrivacy: {},
    amenities: {},
    av: {},
    outdoor: {},
    windows: {},
    venueHighlights: {},
    editorialRecognition: {},
    topHospitalityGroups: {},
  }

  const applyGoogleRatingThresholds = (counts: Array<{ value: string; count: number }>) => {
    const rawGoogleRatings = counts
      .map(({ value, count }) => ({
        value: parseFloat(value),
        count,
      }))
      .filter(({ value }) => !isNaN(value))

    const ratingThresholds = [4.5, 4.2, 4.0, 3.5]
    for (const threshold of ratingThresholds) {
      const cumulativeCount = rawGoogleRatings.filter(({ value }) => value >= threshold).reduce((sum, { count }) => sum + count, 0)
      facetCounts.googleRating[String(threshold)] = cumulativeCount
    }
  }

  // Process each result in the same order as the searches
  for (let i = 0; i < response.results.length; i++) {
    const result = response.results[i] as any
    const [categoryKey] = categories[i]
    const facetCountsRaw = result.facet_counts || []

    if (facetCountsRaw.length === 0) continue

    const facet = facetCountsRaw[0]
    const counts = facet.counts as Array<{ value: string; count: number }>

    // Special handling for Google Rating - aggregate by thresholds
    if (categoryKey === 'googleRating') {
      applyGoogleRatingThresholds(counts)
      continue
    }

    const mapping = getCategoryMapping(categoryKey)

    for (const { value, count } of counts) {
      // Map the value to a label if a mapping exists, otherwise use as-is
      const label = mapping ? (mapping[value] ?? value) : value
      facetCounts[categoryKey][label] = count
    }
  }
  return facetCounts
}

type VenueFacetSearchResult = {
  facet_counts?: Array<{
    field_name?: string
    counts?: Array<{ value?: string | number; count?: number }>
  }>
}

type VenueFacetMultiSearchResponse = {
  results: VenueFacetSearchResult[]
}

type VenueCountMultiSearchResponse = {
  results: Array<{ found?: number }>
}

type VenueFacetSearch = {
  collection: 'spaces'
  q: '*'
  page: number
  per_page: number
  filter_by: string
  facet_by: 'venue_id'
  max_facet_values: number
}

const VENUE_FACET_CHUNK_SIZE = 200

export const getVenueFacetCountsFromTypesense = async (params: ListSpacesParams, venueIds: ReadonlyArray<number>, options?: { signal?: AbortSignal }): Promise<Record<number, number>> => {
  const ids = Array.from(new Set(venueIds.filter((id) => Number.isFinite(id) && id > 0)))
  if (ids.length === 0) return {}

  const paramsWithoutVenueTagFilter = { ...params }
  delete paramsWithoutVenueTagFilter.venue_ids

  const baseClauses = buildFilterClauses(paramsWithoutVenueTagFilter)
  const searches: VenueFacetSearch[] = []
  for (let i = 0; i < ids.length; i += VENUE_FACET_CHUNK_SIZE) {
    const chunk = ids.slice(i, i + VENUE_FACET_CHUNK_SIZE)
    searches.push({
      collection: 'spaces',
      q: '*',
      page: 1,
      per_page: 0,
      filter_by: [...baseClauses, `venue_id:=[${chunk.join(',')}]`].join(' && '),
      facet_by: 'venue_id',
      max_facet_values: chunk.length,
    })
  }

  const response = (await typesenseClient.multiSearch.perform({ searches }, {}, { abortSignal: options?.signal })) as VenueFacetMultiSearchResponse
  const out: Record<number, number> = {}

  for (const result of response.results) {
    const venueFacet = (result.facet_counts ?? []).find((facet) => facet.field_name === 'venue_id') ?? result.facet_counts?.[0]
    for (const c of venueFacet?.counts ?? []) {
      const id = typeof c.value === 'number' ? c.value : Number(c.value)
      if (Number.isFinite(id) && typeof c.count === 'number') out[id] = c.count
    }
  }

  return out
}
